import { useCallback, useMemo, useEffect } from "react";
import { useRequest } from "ahooks";
import { createForm } from "@formily/core";
import {
  Form,
  FormItem,
  Input,
  Select,
  DatePicker,
  FormButtonGroup,
  Submit,
} from "@formily/antd";
import { message, Modal } from "antd";
import { createSchemaField } from "@formily/react";
import { useParams, useNavigate } from "react-router-dom";
import {
  announcement as announcementService,
  setting as settingService,
} from "@/services";
import { useAuthContext } from "@/components/auth/AuthProvider";
import moment from "moment";
import ContentEditor from "./ContentEditor";
import AttachmentUpload from "./AttachmentUpload";
import MultipleDepartmentSelect from "./MultipleDepartmentSelect";
import PublishButton from "./PublishButton";
import styles from "./index.module.less";

const schema = {
  type: "object",
  properties: {
    column: {
      type: "stirng",
      title: "栏目",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Select",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        style: { width: "200px" },
      },
      enum: [
        { label: "公告", value: 0 },
        { label: "成刊", value: 1 },
        { label: "学习交流", value: 2 },
      ],
      "x-reactions": {
        target: "publication",
        fulfill: {
          state: {
            display: "{{$self.value===1?'visible':'none'}}",
          },
        },
      },
    },
    publication: {
      type: "stirng",
      title: "刊物",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Select",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        style: { width: "200px" },
      },
      "x-reactions": "{{listPublication}}",
    },
    type: {
      type: "stirng",
      title: "类型",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Select",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        style: { width: "200px" },
      },
      enum: [
        { label: "工作通知", value: 0 },
        { label: "约稿通知", value: 1 },
      ],
      "x-reactions": {
        dependencies: ["column"],
        fulfill: {
          state: {
            display: "{{$deps[0]===0?'visible':'none'}}",
          },
        },
      },
    },
    sendee: {
      type: "number",
      title: "接收单位",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "MultipleDepartmentSelect",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        token: "{{{token}}}",
        role: "{{{role}}}",
        style: { width: "200px" },
      },
    },
    title: {
      type: "stirng",
      title: "标题",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
    attachments: {
      type: "stirng",
      title: "附件",
      "x-decorator": "FormItem",
      "x-component": "AttachmentUpload",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        token: "{{{token}}}",
        accept: "image/*,.pdf,.doc,.docx",
      },
    },
    content: {
      type: "stirng",
      title: "内容",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "ContentEditor",
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
    contacts: {
      type: "stirng",
      title: "联系人",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
    contact_number: {
      type: "stirng",
      title: "联系电话",
      format: "phone",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
    published_at: {
      type: "string",
      title: "发布日期",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "DatePicker",
      "x-component-props": {
        style: { width: "180px" },
        placeholder: "请选择发布日期",
        disabled: true,
        showTime: true,
      },
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
  },
};

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    Select,
    ContentEditor,
    AttachmentUpload,
    DatePicker,
    MultipleDepartmentSelect,
  },
});

function AnnouncementForm() {
  const { announcementId } = useParams();
  const navigate = useNavigate();
  const form = useMemo(
    () => createForm({ validateFirst: true, announcementId }),
    [announcementId]
  );
  const auth = useAuthContext();
  const { loading, run } = useRequest(
    (values) => {
      const { attachments = [], sendee = [] } = values;
      if (announcementId)
        return announcementService.updateAnnouncement(
          announcementId,
          {
            ...values,
            records: sendee,
            attachments: attachments.map((a) => a.id || a.response.id),
          },
          auth.token
        );
      else
        return announcementService.createAnnouncement(
          {
            ...values,
            attachments: attachments.map((a) => a.response.id),
            records: sendee,
          },
          auth.token
        );
    },
    {
      manual: true,
      onSuccess: (data) => {
        if (data.status_code === 400) {
          const { status_code, ...rest } = data;
          const messages = Object.values(rest).flat();
          message.error(messages.join(", "));
        } else {
          message.success("操作成功");
          navigate("/workshop/announce/draft");
        }
      },
    }
  );

  const listPublication = useCallback(
    (field) => {
      settingService.listPublication({}, auth.token).then((data) => {
        const dataSource = data.results.map((type) => ({
          label: type.name,
          value: type.id,
        }));
        field.dataSource = dataSource;
        field.loading = false;
      });
    },
    [auth]
  );

  useEffect(() => {
    Modal.warning({
      title: "严禁使用本系统上报、发布及保存涉密信息",
      okText: "我知道了",
    });
  }, []);

  useEffect(() => {
    if (!announcementId) {
      form.setInitialValues({
        contacts: auth.name,
        contact_number: auth.phone,
        published_at: moment().format("YYYY-MM-DD HH:mm:ss"),
      });
    } else {
      announcementService
        .getAnnouncement(announcementId, auth.token)
        .then((data) => {
          const { column, publication, subjects, records, ...rest } = data;
          const initialValues =
            column === 1
              ? {
                  ...rest,
                  column,
                  publication: publication.id,
                  sendee: records.map((r) => r.department),
                }
              : {
                  ...rest,
                  column,
                  sendee: records.map((r) => r.department),
                };
          form.setInitialValues(initialValues);
        });
    }
  }, [announcementId, auth, form]);

  return (
    <div className={styles.announcementForm}>
      <div className="announcement-title">发布新公告</div>
      <div className="announcement-body">
        <Form form={form} labelCol={4} wrapperCol={20}>
          <SchemaField
            schema={schema}
            scope={{
              token: auth.token,
              role: auth.roles,
              listPublication,
            }}
          />
          <FormButtonGroup.FormItem>
            <Submit onSubmit={(values) => run(values)} loading={loading}>
              保存
            </Submit>
            <PublishButton form={form} announcementId={announcementId} />
          </FormButtonGroup.FormItem>
        </Form>
      </div>
    </div>
  );
}

export default AnnouncementForm;
